import { DisplaySettings } from 'types';

export type SaveEffect = { type: 'idle' } | { type: 'patch'; fields: Partial<DisplaySettings>; generation: number };

export interface SettleResult {
  /** False when this response is not the request currently in flight. Ignore it. */
  accepted: boolean;
  /** Saved copy to store, including edits that have not reached Dynamo yet. */
  saved?: Partial<DisplaySettings>;
  effect: SaveEffect;
}

let serverSaved: Partial<DisplaySettings> = {};
let pending: Partial<DisplaySettings> = {};
let inFlightGeneration: number | null = null;
let inFlightFields: Partial<DisplaySettings> = {};
let generation = 0;
/** GETs created and not yet settled. A second sign-in load must not drop the first. */
let loadsInFlight = 0;
/** Fields queued while a GET is outstanding, including ones whose PATCH already succeeded. */
let editedDuringLoad = new Set<keyof DisplaySettings>();

export function resetDisplaySettingsSaveState(): void {
  serverSaved = {};
  pending = {};
  inFlightGeneration = null;
  inFlightFields = {};
  generation = 0;
  loadsInFlight = 0;
  editedDuringLoad = new Set();
}

/** A display-settings GET was created. Edits until it settles are kept when it returns. */
export function beginDisplaySettingsLoad(): void {
  loadsInFlight += 1;
}

/** A display-settings GET failed or was aborted. Do not apply a body. */
export function endDisplaySettingsLoad(): void {
  if (loadsInFlight === 0) return;
  loadsInFlight -= 1;
  if (loadsInFlight === 0) editedDuringLoad = new Set();
}

/** Record one cog-field change. Does not read the other live viewer flags. */
export function queueDisplaySetting(
  field: keyof DisplaySettings,
  value: DisplaySettings[keyof DisplaySettings],
): SaveEffect {
  pending = { ...pending, [field]: value };
  if (loadsInFlight > 0) editedDuringLoad.add(field);
  return nextPatch();
}

/**
 * A display-settings GET arrived. Fields edited locally since that GET was created win,
 * including a PATCH that already succeeded. Other stored fields still apply. This does not start a write.
 */
export function receiveDisplaySettings(server: Partial<DisplaySettings> | null | undefined): Partial<DisplaySettings> {
  const fromServer = { ...(server ?? {}) };
  if (loadsInFlight === 0) {
    serverSaved = fromServer;
    return currentSaved();
  }

  const local = currentSaved();
  const next = { ...fromServer };
  editedDuringLoad.forEach((field) => copyField(next, local, field));
  serverSaved = next;
  loadsInFlight -= 1;
  if (loadsInFlight === 0) editedDuringLoad = new Set();
  return currentSaved();
}

export function settleDisplaySettingsSave(requestGeneration: number, ok: boolean): SettleResult {
  if (inFlightGeneration !== requestGeneration) {
    return { accepted: false, effect: { type: 'idle' } };
  }

  const sentFields = inFlightFields;
  inFlightGeneration = null;
  inFlightFields = {};

  if (!ok) {
    pending = { ...sentFields, ...pending };
    return { accepted: true, saved: currentSaved(), effect: { type: 'idle' } };
  }

  serverSaved = { ...serverSaved, ...sentFields };
  const effect = nextPatch();
  return { accepted: true, saved: currentSaved(), effect };
}

/** Copy to keep in Redux, including edits that have not reached Dynamo yet. */
export function savedDisplaySettingsCopy(): Partial<DisplaySettings> {
  return currentSaved();
}

function currentSaved(): Partial<DisplaySettings> {
  return { ...serverSaved, ...inFlightFields, ...pending };
}

function copyField(
  target: Partial<DisplaySettings>,
  source: Partial<DisplaySettings>,
  field: keyof DisplaySettings,
): void {
  const value = source[field];
  if (value !== undefined) Object.assign(target, { [field]: value });
}

function nextPatch(): SaveEffect {
  if (inFlightGeneration !== null || Object.keys(pending).length === 0) {
    return { type: 'idle' };
  }

  const fields = { ...pending };
  pending = {};
  generation += 1;
  inFlightGeneration = generation;
  inFlightFields = fields;
  return { type: 'patch', fields, generation };
}
